import { CONTRACT_TERMS_ENDPOINT } from "../../../../../consts";
import { useCRUD } from "../../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { ContractTerm, ContractTermPrice } from "../../../../../types/contracts";
import CustomCircularProgress from "../../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Typography, Divider } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../../components/ui/breadcrumps/breadcrumbsContext";
import { EquipmentCounter } from "../../../../../types/equipments";
import { formatCounterValue, formatNumber } from "../../../../../utils";
import {
  getContractTermDisplayLabel,
  groupContractTermPricesByEquipment,
  isCommonCounterContractTerm,
} from "../../../../../utils/settlementContractTerms";
import EntityReferenceLink from "../../../../../components/ui/entityReference/entityReferenceLink.component";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

const renderPriceDetails = (
  price: ContractTermPrice,
  t: (key: string) => string,
  showIncludedInRent: boolean,
) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    <DetailRow label={t("Kind")}>
      <Typography variant="subtitle1">
        {(price.start_counter as EquipmentCounter)?.kind_display || "-"}
      </Typography>
    </DetailRow>
    <DetailRow label={t("Start Counter")}>
      <Typography variant="subtitle1">
        {formatCounterValue((price.start_counter as EquipmentCounter)?.value)}
      </Typography>
    </DetailRow>
    {price.end_counter && (
      <DetailRow label={t("End Counter")}>
        <Typography variant="subtitle1">
          {typeof price.end_counter === "string"
            ? formatCounterValue(price.end_counter)
            : formatCounterValue((price.end_counter as EquipmentCounter)?.value)}
        </Typography>
      </DetailRow>
    )}
    <DetailRow label={t("Net price")} noBorder={!showIncludedInRent}>
      <Typography variant="subtitle1">{price.price ? `${formatNumber(price.price)} zł` : "-"}</Typography>
    </DetailRow>
    {showIncludedInRent && (
      <DetailRow label={t("Included in Rent")} noBorder>
        <Typography variant="subtitle1">{price.included_in_rent || "-"}</Typography>
      </DetailRow>
    )}
  </Box>
);

export const ContractTermDetail = () => {
  const { id, termId } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(CONTRACT_TERMS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [contractTerm, setContractTerm] = useState<ContractTerm | null>(null);

  useEffect(() => {
    const fetchContractTerm = async () => {
      if (id && termId) {
        const response = await get(termId);
        const breadcrumbPath = `/contracts/${id}/equipment/${termId}`;
        setOverride(
          breadcrumbPath,
          response.data
            ? getContractTermDisplayLabel(response.data, t, String(response.data.id))
            : ""
        );
        setContractTerm(response.data);
      }
    };

    const handleContractTermUpdated = () => {
      fetchContractTerm();
    };

    window.addEventListener("contractTermUpdated", handleContractTermUpdated);
    fetchContractTerm();
    return () => {
      window.removeEventListener("contractTermUpdated", handleContractTermUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!contractTerm) {
    return <CustomCircularProgress />;
  }

  const displayLabel = getContractTermDisplayLabel(
    contractTerm,
    t,
    t("Contract Position")
  );
  const isCommonCounter = isCommonCounterContractTerm(contractTerm);
  const priceGroups = groupContractTermPricesByEquipment(contractTerm);

  return (
    <Box sx={basicInfoContainerStyle}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {displayLabel}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {(contractTerm.equipments?.length ?? 0) > 0 && (
            <DetailRow label={t("Equipment")}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {contractTerm.equipments?.map((equipment) => (
                  <EntityReferenceLink
                    key={equipment.id}
                    entityType="equipment"
                    entityId={equipment.id}
                    label={equipment.full_name || "-"}
                    canPreview={true}
                    stopPropagation={false}
                  />
                ))}
              </Box>
            </DetailRow>
          )}
          <DetailRow label={t("Kind")}>
            <Typography variant="subtitle1">{contractTerm.kind_display || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Settlement Cycle")}>
            <Typography variant="subtitle1">
              {contractTerm.settlement_cycle_display || "-"}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Settlement kind")}>
            <Typography variant="subtitle1">
              {contractTerm.settlement_kind_display || "-"}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Next Settlement Date")}>
            <Typography variant="subtitle1">{contractTerm.next_settlement_date || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Net rent price")} noBorder>
            <Typography variant="subtitle1">{formatNumber(contractTerm.rent_price)} zł</Typography>
          </DetailRow>
        </Box>
      </Box>

      {contractTerm.settlement_kind !== "RR" && (
        <>
          <Divider />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t("Prices")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {isCommonCounter
                ? priceGroups.map((group) => (
                    <Box
                      key={group.equipmentId}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          bgcolor: "action.hover",
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {group.equipmentName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1.5 }}>
                        {group.prices.map((price, index) => (
                          <Box
                            key={`${group.equipmentId}-${index}`}
                            sx={{
                              border: group.prices.length > 1 ? "1px solid" : "none",
                              borderColor: "divider",
                              borderRadius: group.prices.length > 1 ? 1 : 0,
                              overflow: "hidden",
                            }}
                          >
                            {renderPriceDetails(price, t, contractTerm.settlement_kind !== "RA")}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))
                : contractTerm.prices?.map((price, index) => (
                    <Box
                      key={index}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      {renderPriceDetails(price, t, contractTerm.settlement_kind !== "RA")}
                    </Box>
                  ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
